<template>
  <div id="merchant-page">
    <page-header :title="$t('FOD Collections')">
      <span v-if="$store.state.AppActiveUser.userRole === 'merchant'">
        <vs-button
          @click="openFeesModal('add')"
          color="primary"
          icon-pack="feather"
          icon="icon-plus"
          size="large"
        >
        </vs-button>
      </span>
    </page-header>
    <div class="my-4 leading-7" v-if="$store.state.AppActiveUser.userRole === 'merchant'">
      <p class="md:text-lg text-black mt-2"> {{ $t('Now you can set the amount you’d like us to collect if a customer refuses their shipment! 🚚💰') }} </p>
      <p class="md:text-lg text-black mt-2"> {{ $t('From this page, you can easily define the “Fee on Delivery” (FOD) - the collection amount in case of refusal - for each governorate individually.') }} </p>
      <hr class="my-4 text-black" />
      <p class="md:text-lg text-black mt-2"> {{ $t('Here’s how it works:') }} </p>
      <ul class="list-disc list-inside mb-2 ml-6">
        <li class="text-base text-black">{{ $t('Effective timing: Any values entered here apply starting from 8:00 AM. Entries made after 8:00 AM will take effect the next business day.') }}</li>
        <li class="text-base text-black">{{ $t('Clear communication: If a shipment is headed to one of the governorates added here and the customer refuses to accept it, our courier will inform them that the sender has set a fee to be collected in case of refusal.') }}</li>
        <li class="text-base text-black">{{ $t('Automated process: At the same time, ShipBlu automatically creates a “Cash Collection Request” that appears in your Cash Collections page.') }}</li>
        <li class="text-base text-black">{{ $t('Seamless reconciliation: Once the FOD amount is collected, the request updates to “Collected.” The amount is then transferred to you along with your other collections, while the service fee for FOD Collection is charged to your account, in addition to your standard RTO shipping fee.') }}</li>
        <li class="text-base text-black">{{ $t('No extra fees: Any amounts collected under FOD are not subject to additional collection charges.') }}</li>
        <li class="text-base text-black">{{ $t('If collection fails: If, for any reason, the FOD amount cannot be collected, the Cash Collection Request updates to “Failed.” In this case, no FOD service fees will be charged - only your standard RTO shipping fee applies.') }}</li>
        <li class="text-base text-black">{{ $t('Customer confirmation: To ensure accuracy, if a customer refuses to pay the FOD amount, they immediately receive a confirmation message notifying them that the courier has logged their refusal.') }}</li>
      </ul>
      <p class="md:text-lg text-black"> {{ $t('✨ With this feature, you can start saving on the cost of returns - helping you protect your bottom line every single day!') }} </p>
      <p class="md:text-lg text-black"> {{ $t('For any further questions, just reach out to us via chat - we’ll be more than happy to help. 💙') }} </p>
    </div>
    <template>
      <shipblu-table
        :sst="true"
        multiple
        hideHeader
        v-model="selected"
        pagination
        :max-items="maximumItems"
        :data="feesOnDelivery"
        :tableLoader="tableLoader"
      >
        <template slot="thead">
          <shipblu-th>{{$t('Governorate')}}</shipblu-th>
          <shipblu-th>{{$t('Cash Amount')}}</shipblu-th>
          <shipblu-th class="flex justify-center" v-if="$store.state.AppActiveUser.userRole === 'merchant'">
            <feather-icon icon="ChevronDownIcon" svgClasses="h-5 w-5"/>
          </shipblu-th>
        </template>

        <template slot-scope="{ data }">
          <shipblu-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">

            <shipblu-td class="sm:hidden block col-span-2 p-0 order-3">
              <!-- line -->
              <p style="background-color: #DCDCDC;height: 1px;"></p>
            </shipblu-td>

            <shipblu-td :data="data[indextr].governorate" class="text-left sm:order-3 order-16 sm:pointer-events-none">
              <div class="flex sm:static absolute top-3 gap-2 items-start justify-start transform-50">
                <p class="sm:text-base text-lg flex gap-1 items-start justify-center">
                  {{ $t(data[indextr].governorate && data[indextr].governorate.name) }}
                </p>
              </div>
            </shipblu-td>

            <shipblu-td class="order-4" :data="data[indextr].cash_amount">
              <p class="sm:hidden block text-secondary text-sm mb-2">{{$t('Cash Amount')}}</p>
              <p class="sm:text-base text-sm">
                {{ parseInt(data[indextr].cash_amount) > 0 ? (data[indextr].cash_amount).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2}) : 'N/A' }} {{$t('EGP')}}
              </p>
            </shipblu-td>

            <shipblu-td class="order-2" v-if="$store.state.AppActiveUser.userRole === 'merchant'">
              <div class="md:text-center text-right">
                <vs-dropdown
                  vs-trigger-click
                  @click="selected = []"
                  class="dd-actions cursor-pointer"
                >
                  <span class="material-symbols-outlined text-2xl">more_horiz</span> 
                  <vs-dropdown-menu style="width: 190px">
                    <vs-dropdown-item @click="openFeesModal('edit', data[indextr])">
                      <span class="flex items-center">
                        <feather-icon
                          icon="EditIcon"
                          svgClasses="h-4 w-4"
                          class="mr-2"
                        />
                        <span>{{$t('Edit')}}</span>
                      </span>
                    </vs-dropdown-item>
                    <vs-dropdown-item @click="deleteFees(data[indextr])">
                      <span class="flex items-center">
                        <feather-icon
                          icon="TrashIcon"
                          svgClasses="h-4 w-4"
                          class="mr-2"
                        />
                        <span>{{$t('Delete')}}</span>
                      </span>
                    </vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </shipblu-td>
          </shipblu-tr>
        </template>
      </shipblu-table>
      <shipblu-pagination :maximumItems="maximumItems" :noOptions="true" :totalRows="totalRows" :currentPage="currentPage" @currentPage="currentPage = $event" />
    </template>

    <!-- Add/Edit Fee Modal -->
    <fees-modal :addFeesOnDeliveryModal="addFeesOnDeliveryModal" :type="type" :data="feesData" :merchantId="merchantId" @addFeesOnDeliveryModal="addFeesOnDeliveryModal = $event" @loadData="loadData"/>
  </div>
</template>

<script>
import {sendRequest} from '../../http/axios/requestHelper.js'
import common  from '@/assets/utils/common'
import ShipbluTable from '../../layouts/components/NewShipBluTable.vue'
import ShipbluTr from '../../layouts/components/ShipBluTr.vue'
import ShipbluTh from '../../layouts/components/ShipBluTh.vue'
import ShipbluTd from '../../layouts/components/ShipBluTd.vue'
import i18nData from '../../i18n/i18nData.js'
import ShipbluPagination from '../../layouts/components/ShipbluPagination.vue'
import FeesModal from '../merchant/components/AddFeesDelivery.vue'

export default {
  data () {
    return {
      common,
      maximumItems: process.env.VUE_APP_MAXIMUM_ITEMS_TABLE,
      merchantURL: 'api/v1/merchants/',
      selected: [],
      deleteData: {},
      feesOnDelivery: [],
      addFeesOnDeliveryModal: false,
      type: '',
      feesData: {
        cash_amount: '',
        governorate: null
      },
      offset: 0,
      totalRows: 0,
      currentPage: (this.$route.query.page &&  Number(this.$route.query.page) > 0) ? Number(this.$route.query.page) : 1,
      tableLoader: false,
      merchantId: ''
    }
  },
  watch: {
    currentPage () {
      this.offset = (this.currentPage - 1) * this.maximumItems
      this.loadData()
    }
  },
  mounted () {
    if (this.$store.state.AppActiveUser.userRole === 'merchant') {
      const merchantData = common.checkMerchantInLocalStorage(this)
      merchantData.then(results => {
        this.merchantId = results.merchantData.id
        this.loadData()
      })
    } else {
      this.merchantId = this.$route.params.merchantId || this.$route.params.merchantID
      this.loadData()
    }
  },
  methods: {
    loadData () {
      this.tableLoader = true
      const query = `?offset=${this.offset}&limit=${this.maximumItems}`
      const url = `${this.merchantURL}${this.merchantId}/fees-on-delivery/${query}`
      
      sendRequest(true, false, this, url, 'get', null, true,
        (response) => {
          this.feesOnDelivery = response.data.results || response.data
          this.totalRows = response.data.count || this.feesOnDelivery.length
          this.tableLoader = false
        }
      )
    },
    openFeesModal (type, data) {
      this.type = type
      if (type === 'add') {
        this.feesData = {
          cash_amount: '',
          governorate: null
        }
      } else {
        this.feesData = { 
          ...data,
          governorate: data.governorate && data.governorate.id ? data.governorate.id : data.governorate
        }
      }
      this.addFeesOnDeliveryModal = true
    },
    deleteFees (data) {
      this.deleteData = data
      this.$vs.dialog({
        color: 'danger',
        title: i18nData[this.$i18n.locale]['Confirm'],
        text: i18nData[this.$i18n.locale]['Are you sure to delete?'],
        accept: this.confirmDelete
      })
    },
    confirmDelete () {
      const url = `${this.merchantURL}${this.merchantId}/fees-on-delivery/${this.deleteData.id}/`
      sendRequest(false, false, this, url, 'delete', null, true,
        () => {
          this.$vs.notify({
            color:'success',
            title:'Success',
            text:i18nData[this.$i18n.locale]['Fees has been deleted successfully'],
            position: 'top-center'
          })
          this.loadData()
        }
      )
    }
  },
  components: {
    ShipbluTable,
    ShipbluTr,
    ShipbluTh,
    ShipbluTd,
    ShipbluPagination,
    FeesModal
  }
}
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/email.scss";
.con-pagination-table{
  display: none;
}
</style>

<style scoped>
ul, ol {
    list-style-type: unset;
}
</style>